import {
  getProductIncludedItems,
  getProductUpsellItems,
  addOnNameLooksDrinkLike,
} from '@/lib/productComposition';
import {
  normalizeAssistantText,
  tokenizeQuery,
  ASSISTANT_STOP_WORDS,
} from '@/lib/assistant/assistantTextUtils';
import { applyRegionalOrderSpanish } from '@/lib/assistant/assistantRegionalSpanish';
import {
  expandOrderProductQuery,
  resolveCatalogNicknameProduct,
  scorePiezasCountMatch,
} from '@/lib/assistant/orderProductQuery';
import { isPromoVisibleInCatalog, promoToCatalogItem } from '@/lib/menu/catalogPromos';

function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Match de alias en el blob del producto: límites de palabra (evita “asado” en cualquier lado, “ebi” dentro de otra palabra).
 * Frases con espacio se matchean como unidad.
 */
export function aliasMatchesInBlob(blob, phraseNorm) {
  const p = String(phraseNorm || '').trim();
  if (!blob || !p || p.length < 2) return false;
  if (p.includes(' ')) {
    const rx = new RegExp(
      '(?:^|[^a-z0-9])' + p.split(/\s+/).map(escapeRegExp).join('\\s+') + '(?:$|[^a-z0-9])',
      'i'
    );
    return rx.test(blob);
  }
  return new RegExp('\\b' + escapeRegExp(p) + '\\b', 'i').test(blob);
}

function aliasMatchesInQuery(n, phraseNorm) {
  return aliasMatchesInBlob(String(n || ''), phraseNorm);
}

/** Variantes simples singular/plural en ES (tacos↔taco). */
function expandTermVariants(t) {
  const norm = normalizeAssistantText(String(t || ''));
  const set = new Set([norm]);
  if (norm.length >= 4 && norm.endsWith('s') && !norm.endsWith('ss')) {
    set.add(norm.slice(0, -1));
  }
  if (norm.length >= 3 && !norm.endsWith('s')) {
    set.add(norm + 's');
  }
  return [...set].filter((x) => x.length >= 2);
}

function expandSynonymListWithPlurals(synonyms) {
  const out = new Set();
  for (const s of synonyms) {
    for (const v of expandTermVariants(s)) {
      if (v.length >= 2) out.add(v);
    }
  }
  return [...out];
}

function productSearchBlobForAssistant(product, menuData) {
  let blob = productIngredientSearchBlob(product);
  const promos = (menuData?.promos || []).filter((pr) => pr.is_active !== false);
  for (const pr of promos) {
    const ids = pr.product_ids;
    if (!Array.isArray(ids) || !ids.length) continue;
    if (!ids.some((id) => String(id) === String(product.id))) continue;
    blob = normalizeAssistantText([blob, pr.name, pr.description].filter(Boolean).join(' '));
  }
  return blob;
}

export function getProductSearchBlob(product, menuData) {
  return menuData ? productSearchBlobForAssistant(product, menuData) : productIngredientSearchBlob(product);
}
export function rankProductMatches(rawQuery, products, menuData = null) {
  const tokens = tokenizeQuery(rawQuery);
  const qn = normalizeAssistantText(rawQuery);
  if (!tokens.length && qn.length < 2) return [];
  const scored = [];
  for (const p of products) {
    if (p.availability === 'hidden') continue;
    const name = normalizeAssistantText(p.name || '');
    const desc = normalizeAssistantText(p.description || '');
    const cat = normalizeAssistantText(p.category || '');
    const blob = menuData ? getProductSearchBlob(p, menuData) : '';
    let score = 0;
    if (qn.length >= 2) {
      if (name === qn) score += 12;
      else if (name.includes(qn) && qn.length >= 3) score += 6;
      else if (qn.includes(name) && name.length >= 4) score += 5;
      else if (blob && aliasMatchesInBlob(blob, qn)) score += 4;
    }
    for (const tok of tokens) {
      const syns = expandIngredientTermGroup(tok);
      let add = 0;
      for (const s of syns) {
        if (!s || s.length < 2) continue;
        if (name.includes(s)) add = Math.max(add, 4);
        else if (name.split(/\s+/).some((part) => part.startsWith(s))) add = Math.max(add, 2.5);
        if (desc.includes(s)) add = Math.max(add, 1.2);
        if (cat.includes(s)) add = Math.max(add, 1.5);
        if (blob && aliasMatchesInBlob(blob, s)) add = Math.max(add, 2.8);
      }
      score += add;
    }
    if (tokens.length >= 2) {
      const nameHits = tokens.filter(
        (tok) =>
          name.includes(tok) ||
          name.split(/\s+/).some((part) => part.startsWith(tok)) ||
          (blob && aliasMatchesInBlob(blob, tok))
      ).length;
      score += nameHits * 1.5;
      if (nameHits === tokens.length) score += 3;
      for (const tok of tokens) {
        const inName =
          name.includes(tok) ||
          name.split(/\s+/).some((part) => part.startsWith(tok)) ||
          (blob && aliasMatchesInBlob(blob, tok));
        if (!inName && /^(grill|doble|simple|kids|classic|smash|nugget)$/.test(tok)) {
          score -= 1.5;
        }
      }
      if (tokens.includes('kids') && !/\bduo\b/.test(qn)) {
        if (/^kids\b/.test(name)) score += 2.5;
        if (/\bduo\b/.test(name)) score -= 2.5;
      }
    }
    score += scorePiezasCountMatch(qn, name);
    if (score > 0) scored.push({ product: p, score });
  }
  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return (Number(b.product.sales_count) || 0) - (Number(a.product.sales_count) || 0);
  });
  return scored;
}
function listRemovable(product) {
  const r = product?.removable_ingredients;
  if (!Array.isArray(r) || r.length === 0) return null;
  return r.map((x) => (typeof x === 'string' ? x : x?.name)).filter(Boolean);
}
const INGREDIENT_META_TERM_BLOCKLIST = new Set(
  normalizeAssistantText(
    'menu menus carta cartas dia diario del plato platos carta del dia menu del dia'
  )
    .split(/\s+/)
    .filter((w) => w.length >= 2)
);
export function findProductsStrictIngredientTokens(products, tokens, menuData = null) {
  if (!tokens?.length) return [];
  const groups = tokens
    .map((t) => expandSynonymListWithPlurals(expandIngredientTermGroup(t)))
    .filter((g) => g.length);
  if (!groups.length) return [];
  const vis = visibleMenuProducts(products);
  const out = [];
  for (const p of vis) {
    const blob = getProductSearchBlob(p, menuData);
    const ok = groups.every((syns) => syns.some((s) => aliasMatchesInBlob(blob, s)));
    if (ok) out.push(p);
  }
  out.sort((a, b) => (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0));
  return out.slice(0, 8);
}
function stripOrderMentionNoise(s) {
  let t = applyRegionalOrderSpanish(String(s || '').trim());
  let guard = 0;
  while (guard++ < 12) {
    const prev = t;
    t = t
      .replace(
        /^(quiero|dame|traeme|tráeme|pedir|necesito|me\s+das|me\s+traes|ponme|deme|tirame|tírame|pasame|pásame|mandame|mándame|anotame|apuntame|una|un|unos|unas|el|la|los|las)\b[!?.,\s:]*/i,
        ''
      )
      .trim();
    t = t.replace(/^(por\s+favor|pf|pls|favor)\b[!?.,\s:]*/i, '').trim();
    if (t === prev) break;
  }
  return t;
}

/**
 * Mejor match de producto para frases largas (“quiero la X sin Y”): prueba texto completo y sin prefijos de pedido.
 */
export function bestProductMatchForAssistantQuery(trimmed, products, menuData = null) {
  const raw = String(trimmed || '').trim();
  const stripped = stripOrderMentionNoise(raw);
  const candidates = [raw, stripped].filter((x, i, a) => x.length >= 2 && a.indexOf(x) === i);
  let bestRanked = null;
  for (const c of candidates) {
    const ranked = rankProductMatches(c, products, menuData);
    if (!ranked.length) continue;
    if (!bestRanked || ranked[0].score > bestRanked[0].score) bestRanked = ranked;
  }
  const best = bestRanked?.[0];
  const bestProduct = best && best.score >= 3 ? best.product : null;
  return { ranked: bestRanked || [], bestProduct };
}

const GENERIC_BEVERAGE_TERMS = [
  'bebida',
  'bebidas',
  'gaseosa',
  'gaseosas',
  'jugo',
  'jugos',
  'agua',
  'lata',
  'schop',
  'refresco',
  'chela',
  'cheve',
  'birra',
  'soda',
];

function isMenuBeverageProduct(product, menuData = null) {
  const blob = getProductSearchBlob(product, menuData);
  const cat = normalizeAssistantText(product?.category || '');
  return (
    GENERIC_BEVERAGE_TERMS.some((t) => aliasMatchesInBlob(blob, t)) ||
    /\b(bebida|gaseosa|jugo|bar|licor|cerveza)\b/.test(cat)
  );
}

function findMenuBeverageMatch(query, products, menuData = null) {
  const catalog = expandProductsForCompositeOrder(products);
  const drinkPool = catalog.filter((p) => isMenuBeverageProduct(p, menuData) || p._compositeDrinkAddon);
  if (!drinkPool.length) return null;
  const ranked = rankProductMatches(query, drinkPool, menuData);
  if (ranked[0]?.score >= 2) return ranked[0].product;
  return [...drinkPool].sort((a, b) => (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0))[0];
}

/** Extras (add_ons) únicos de toda la carta, para match de líneas tipo "extra carne". */
export function collectMenuAddOns(products) {
  const byKey = new Map();
  for (const p of visibleMenuProducts(products)) {
    const addons = p?.add_ons;
    if (!Array.isArray(addons)) continue;
    for (const a of addons) {
      const name = String(typeof a === 'string' ? a : a?.name || '')
        .replace(/\s+/g, ' ')
        .trim();
      if (!name) continue;
      const key = normalizeAssistantText(name);
      const price = Number(typeof a === 'object' ? a?.price : 0) || 0;
      const prev = byKey.get(key);
      if (!prev || (price > 0 && !prev.price)) {
        byKey.set(key, { name, price });
      }
    }
  }
  return [...byKey.values()];
}

/** Match de extra de carta ("extra carne", "Extra Carne", etc.). */
export function bestCompositeAddOnMatch(query, products) {
  const raw = String(query || '').trim();
  const qn = normalizeAssistantText(raw);
  if (!qn || qn.length < 3) return null;
  const addons = collectMenuAddOns(products);
  if (!addons.length) return null;

  const exact = addons.find((a) => normalizeAssistantText(a.name) === qn);
  if (exact) return exact;

  const stripped = qn.replace(/^(?:extra|con)\s+/, '').trim();
  if (stripped.length >= 3) {
    const byStrip = addons.find((a) => normalizeAssistantText(a.name) === stripped);
    if (byStrip) return byStrip;
    const containsStrip = addons.find((a) => normalizeAssistantText(a.name).includes(stripped));
    if (containsStrip) return containsStrip;
    const stripInName = addons.find((a) => stripped.includes(normalizeAssistantText(a.name)));
    if (stripInName) return stripInName;
  }

  let best = null;
  let bestScore = 0;
  for (const a of addons) {
    const an = normalizeAssistantText(a.name);
    let score = 0;
    if (an === qn) score = 10;
    else if (qn.includes(an) && an.length >= 4) score = 6;
    else if (an.includes(qn) && qn.length >= 4) score = 5;
    else {
      const qTokens = qn.replace(/^(?:extra|con)\s+/, '').split(/\s+/).filter((t) => t.length >= 3);
      const hits = qTokens.filter((t) => an.includes(t)).length;
      if (hits === qTokens.length && hits > 0) score = 4 + hits;
    }
    if (score > bestScore) {
      bestScore = score;
      best = a;
    }
  }
  return bestScore >= 4 ? best : null;
}

/** ¿La línea parece un agregado configurado en la carta? */
export function looksLikeMenuAddOnLine(query, products) {
  const raw = String(query || '').trim();
  if (!raw || raw.length < 3) return false;
  const qn = normalizeAssistantText(raw);
  if (/^extra\b/i.test(raw) || /^con\s+extra\b/i.test(raw)) {
    return Boolean(bestCompositeAddOnMatch(raw, products));
  }
  if (/^con\s+/.test(qn)) {
    return Boolean(bestCompositeAddOnMatch(raw.replace(/^con\s+/i, ''), products));
  }
  if (/^\+/.test(raw)) {
    return Boolean(bestCompositeAddOnMatch(raw.replace(/^\+\s*/, ''), products));
  }
  return Boolean(bestCompositeAddOnMatch(raw, products));
}

/** Productos de la carta que ofrecen un agregado (para asociar extras pendientes). */
export function productsOfferingAddOn(addon, products) {
  const target = String(addon?.name || '').trim();
  if (!target) return [];
  const owners = [];
  for (const p of visibleMenuProducts(products)) {
    const offered = collectMenuAddOns([p]);
    if (
      offered.some((a) => {
        const an = normalizeAssistantText(a.name);
        const tn = normalizeAssistantText(target);
        if (an === tn) return true;
        const aCore = an.replace(/^extra\s+/, '');
        const tCore = tn.replace(/^extra\s+/, '');
        return aCore === tCore || an.includes(tCore) || tCore.includes(aCore);
      })
    ) {
      owners.push(p);
    }
  }
  return owners;
}

/** Bebidas que en la carta solo existen como extras (add_ons) de otros productos. */
export function collectStandaloneDrinksFromAddons(products) {
  const byKey = new Map();
  for (const p of visibleMenuProducts(products)) {
    const addons = p?.add_ons;
    if (!Array.isArray(addons)) continue;
    for (const a of addons) {
      const name = String(typeof a === 'string' ? a : a?.name || '')
        .replace(/\s+/g, ' ')
        .trim();
      if (!name || !addOnNameLooksDrinkLike(name)) continue;
      const key = normalizeAssistantText(name);
      const price = Number(typeof a === 'object' ? a?.price : 0) || 0;
      const prev = byKey.get(key);
      if (!prev || (price > 0 && !prev.price)) {
        byKey.set(key, { name, price });
      }
    }
  }
  return [...byKey.values()].map(({ name, price }) => ({
    id: `composite-drink:${normalizeAssistantText(name).replace(/\s+/g, '-')}`,
    name,
    price: price > 0 ? price : 1500,
    category: 'Bebidas',
    availability: 'available',
    sales_count: 500,
    _compositeDrinkAddon: true,
  }));
}

/** Catálogo ampliado: productos visibles + bebidas detectadas en extras del menú. */
export function expandProductsForCompositeOrder(products) {
  const vis = visibleMenuProducts(products);
  const drinks = collectStandaloneDrinksFromAddons(products);
  const existingNames = new Set(vis.map((p) => normalizeAssistantText(p.name || '')));
  const extra = drinks.filter((d) => !existingNames.has(normalizeAssistantText(d.name)));
  return [...vis, ...extra];
}

/** Productos + promociones activas del menú (mismo criterio que el catálogo público). */
export function expandCatalogForCompositeOrder(products, menuData = null) {
  const base = expandProductsForCompositeOrder(products);
  const byName = new Map();
  for (const p of base) {
    byName.set(normalizeAssistantText(p.name || ''), p);
  }
  const daily = menuData?.daily_deal_product;
  if (daily?.name) {
    byName.set(normalizeAssistantText(daily.name), daily);
  }
  const promos = (menuData?.promos || []).filter((pr) => isPromoVisibleInCatalog(pr));
  for (const pr of promos) {
    const item = promoToCatalogItem(pr, products);
    if (!item?.name) continue;
    byName.set(normalizeAssistantText(item.name), item);
  }
  if (!promos.length && !daily?.name) return base;
  return [...byName.values()];
}

function pickClearRankedMatch(ranked, minScore = 2) {
  if (!ranked?.length || ranked[0].score < minScore) return null;
  const top = ranked[0];
  const second = ranked[1];
  if (second && top.score - second.score < 0.6 && top.score < 5) return null;
  return top.product;
}

function pickCompositeRankedMatch(ranked, minScore = 2) {
  if (!ranked?.length || ranked[0].score < minScore) return null;
  const top = ranked[0];
  const second = ranked[1];
  if (!second) return top.product;
  if (top.score >= 5.5) return top.product;
  if (top.score - second.score >= 0.55) return top.product;
  if (top.score >= 4 && top.score - second.score >= 0.3) return top.product;
  return null;
}

const FAMILY_MENU_FALLBACK = {
  hamburguesa: /\b(burger|hamburg|smash|grill|bigmati|stack|cheese|principal|especial)\b/i,
  pizza: /\bpizza\b/i,
  empanada: /\bempanada\b/i,
  sushi: /\b(sushi|maki|roll|japon|japones)\b/i,
  completo: /\b(completo|vienesa|hot\s*dog|pancho)\b/i,
  pasta: /\b(pasta|fetucc|spaghetti|raviol|ñoqui)\b/i,
  parrilla: /\b(churrasco|parrill|asado|bife|lomo|entra)\b/i,
  pollo_asado: /\b(pollo|brasa|broaster)\b/i,
  cerveza: /\b(cerveza|schop|beer|ipa|lager)\b/i,
};

function filterProductsForFamily(products, fam, menuData = null) {
  const fromBlob = filterProductsByCategoryKeys(products, fam.keys, menuData);
  if (fromBlob.length) return fromBlob;
  const rx = FAMILY_MENU_FALLBACK[fam.familyId];
  if (!rx) return [];
  return visibleMenuProducts(products)
    .filter((p) => {
      const blob = normalizeAssistantText([p.name, p.category].filter(Boolean).join(' '));
      return rx.test(blob);
    })
    .sort((a, b) => (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0));
}

/**
 * Match de producto para pedidos compuestos: usa la carta del local (nombres, categorías, promos).
 */
export function bestCompositeProductMatch(query, products, menuData = null) {
  const raw = String(query || '').trim();
  if (!raw || !products?.length) return null;
  const vis = expandProductsForCompositeOrder(products);
  const qn = normalizeAssistantText(raw);

  const exact = vis.find((p) => normalizeAssistantText(p.name) === qn);
  if (exact) return exact;

  const stripped = stripOrderMentionNoise(raw);
  const nick = resolveCatalogNicknameProduct(stripped || raw, vis);
  if (nick) return nick;

  const expanded = expandOrderProductQuery(stripped || raw, vis);
  const candidates = [...expanded, raw, stripped].filter(
    (x, i, a) => x.length >= 2 && a.indexOf(x) === i
  );
  let bestRanked = null;
  for (const c of candidates) {
    const ranked = rankProductMatches(c, vis, menuData);
    if (!ranked.length) continue;
    if (!bestRanked || ranked[0].score > bestRanked[0].score) bestRanked = ranked;
  }

  const clear = pickCompositeRankedMatch(bestRanked, 2);
  if (clear) return clear;

  const fam = matchProductCategory(qn);
  if (fam) {
    const pool = filterProductsForFamily(vis, fam, menuData);
    if (pool.length === 1) return pool[0];
    if (pool.length > 1) {
      const subRanked = rankProductMatches(stripped || raw, pool, menuData);
      const sub = pickCompositeRankedMatch(subRanked, 1.5);
      if (sub) return sub;
      if (subRanked[0]?.score >= 3.5) return subRanked[0].product;
      return pool[0];
    }
  }

  if (GENERIC_BEVERAGE_TERMS.some((t) => aliasMatchesInQuery(qn, t))) {
    const drink = findMenuBeverageMatch(raw, vis, menuData);
    if (drink) return drink;
  }

  const { bestProduct, ranked } = bestProductMatchForAssistantQuery(raw, vis, menuData);
  if (bestProduct) return bestProduct;
  return pickCompositeRankedMatch(ranked, 2.5);
}

export function resolveStrongProductMention(trimmed, products) {
  const stripped = stripOrderMentionNoise(trimmed);
  const candidates = [stripped, trimmed].filter((x, i, a) => x.length >= 2 && a.indexOf(x) === i);
  let bestRanked = null;
  for (const c of candidates) {
    const ranked = rankProductMatches(c, products);
    if (!ranked.length) continue;
    if (!bestRanked || ranked[0].score > bestRanked[0].score) bestRanked = ranked;
  }
  if (!bestRanked || bestRanked[0].score < 3.5) return null;
  const top = bestRanked[0];
  const second = bestRanked[1];
  if (second && top.score - second.score < 0.75) {
    if (top.score >= 4 && second.score >= 3) {
      return {
        products: [top.product, second.product],
        headline: 'Te dejo estas dos que calzan; elegí la que sea 👇',
      };
    }
    return null;
  }
  return {
    products: [top.product],
    headline: '¡Buenísima elección 🔥!',
  };
}
export function visibleMenuProducts(products) {
  return (products || []).filter((p) => p.availability !== 'hidden');
}

export function resolveProductsByIds(products, ids) {
  if (!ids?.length) return [];
  const map = new Map(products.map((p) => [String(p.id), p]));
  return ids.map((id) => map.get(String(id))).filter(Boolean);
}

export const INGREDIENT_ALIASES = [
  ['pollo', 'chicken', 'nugget', 'nuggets', 'nuggets de pollo', 'pollo frito', 'popcorn', 'karaage', 'katsu'],
  ['carne', 'vacuno', 'res', 'beef', 'bife', 'lomito', 'meat', 'vacio', 'wagyu'],
  ['cerdo', 'pork', 'chancho', 'costillar', 'ribs', 'panceta', 'bacon'],
  ['camaron', 'camarón', 'camarones', 'shrimp', 'ebi', 'ebiko', 'gamba', 'langostino'],
  ['salmon', 'salmón', 'sake'],
  ['atun', 'atún', 'tuna', 'maguro'],
  ['cangrejo', 'crab', 'kani', 'kanikama', 'surimi'],
  ['pulpo', 'tako', 'octopus'],
  ['huevo', 'egg', 'tamago', 'huevo frito'],
  ['palta', 'aguacate', 'avocado'],
  ['champinon', 'champiñon', 'hongos', 'mushroom', 'shiitake'],
  ['arroz', 'rice', 'gohan'],
  ['alga', 'nori', 'wakame', 'kelp'],
  ['queso', 'cheese', 'mozzarella', 'cheddar', 'parmes'],
  ['pescado', 'fish', 'pez'],
  ['tocino', 'tocineta', 'bacon'],
]
  .map((g) =>
    [...new Set(g.map((x) => normalizeAssistantText(String(x))))].filter((tok) => tok.length)
  )
  .filter((g) => g.length > 0);

/** Términos de estilo sushi: expanden a mariscos/pescados frecuentes en carta (OR dentro del grupo). */
const SUSHI_STYLE_TERMS = new Set(
  normalizeAssistantText(
    'sushi maki makis nigiri uramaki sashimi roll rolls temaki handroll hosomaki futomaki gunkan california philadelphia'
  )
    .split(/\s+/)
    .filter((w) => w.length >= 2)
);

function expandIngredientTermGroup(termNorm) {
  const t = normalizeAssistantText(String(termNorm || ''));
  if (t.length < 2) return [];
  if (SUSHI_STYLE_TERMS.has(t)) {
    const merged = new Set();
    for (const seed of ['salmon', 'atun', 'camaron', 'cangrejo', 'pulpo']) {
      const g = expandIngredientTermGroup(seed);
      g.forEach((x) => merged.add(x));
    }
    return merged.size ? [...merged] : [t];
  }
  for (const group of INGREDIENT_ALIASES) {
    if (group.includes(t)) return group;
  }
  return [t];
}

/**
 * Familias de producto por rubro (texto normalizado). El match en fichas usa límites de palabra.
 * Orden interno irrelevante: gana el alias más largo que calce en la consulta.
 */
export const PRODUCT_FAMILY_ALIASES = [
  {
    id: 'sushi',
    label: 'sushi',
    match: [
      'sushi',
      'maki',
      'makis',
      'nigiri',
      'uramaki',
      'sashimi',
      'yakitori',
      'tonkatsu',
      'teriyaki',
      'edamame',
      'misoshiru',
      'futomaki',
      'hosomaki',
      'gunkan',
      'omakase',
    ],
  },
  {
    id: 'hand_roll',
    label: 'hand rolls o temaki',
    match: ['hand roll', 'handroll', 'handrolls', 'temaki'],
  },
  { id: 'pizza', label: 'pizza', match: ['pizza', 'piza'] },
  {
    id: 'parrilla',
    label: 'churrasco o parrilla',
    match: [
      'churrasco',
      'churrascos',
      'parrillada',
      'parrillero',
      'parrilla',
      'entraña',
      'entrania',
      'punta tranca',
      'punta de tranca',
      'picanha',
      'picana',
      't-bone',
      'tbone',
      'costillar',
      'lomo vetado',
      'lomo liso',
      'bife de chorizo',
      'bife de lomo',
      'matambre',
      'asado de tira',
      'vacío',
      'vacio',
    ],
  },
  {
    id: 'mechada',
    label: 'mechada o sándwich clásico',
    match: ['mechada', 'carne mechada', 'lomito mechado', 'barros luco', 'chacarero', 'italiano chileno'],
  },
  {
    id: 'hamburguesa',
    label: 'hamburguesas',
    match: ['hamburguesa', 'hamburguesas', 'hamburg', 'burger', 'burgers', 'smash', 'smash burger'],
  },
  {
    id: 'arma_burger',
    label: 'arma tu hamburguesa',
    match: ['arma tu', 'armá tu', 'arma la', 'monta tu', 'elige tu', 'armalo', 'armalo tu'],
  },
  {
    id: 'completo',
    label: 'completos o vienesas',
    match: ['completo', 'completos', 'hot dog', 'hotdog', 'vienesa', 'vienesas', 'italiano', 'pancho', 'panchos'],
  },
  { id: 'empanada', label: 'empanadas', match: ['empanada', 'empanadas', 'empanad'] },
  {
    id: 'pasta',
    label: 'pasta',
    match: ['pasta', 'fetuccini', 'fettuccine', 'spaghetti', 'lasagna', 'ravioles', 'ñoquis', 'gnocchi', 'ravioli'],
  },
  {
    id: 'sandwich',
    label: 'sándwiches',
    match: ['sandwich', 'sándwich', 'sandwiches', 'sándwiches', 'panini', 'club sandwich'],
  },
  {
    id: 'mex',
    label: 'tacos o comida mexicana',
    match: [
      'taco',
      'tacos',
      'taquito',
      'taquitos',
      'burrito',
      'burritos',
      'quesadilla',
      'quesadillas',
      'nacho',
      'nachos',
      'fajita',
      'fajitas',
      'chimichanga',
    ],
  },
  { id: 'poke', label: 'poke', match: ['poke', 'pokes'] },
  {
    id: 'pollo_asado',
    label: 'pollo al horno, a la brasa o asado',
    match: [
      'pollo asado',
      'pollo al horno',
      'pollo a la brasa',
      'pollo brasa',
      'pollo a las brasas',
      'pollo spiedo',
      'pollo al spiedo',
      'brocheta de pollo',
    ],
  },
  {
    id: 'pollo_frito',
    label: 'pollo frito',
    match: [
      'pollo frito',
      'broaster',
      'broasted',
      'crispy chicken',
      'pollo crispy',
      'alitas',
      'wings',
      'wing',
    ],
  },
  {
    id: 'papas_topping',
    label: 'papas con topping o cargadas',
    match: [
      'papas locas',
      'papas topping',
      'papas con topping',
      'loaded fries',
      'papas cargadas',
      'papas rebozadas',
    ],
  },
  {
    id: 'cerveza',
    label: 'cerveza',
    match: ['cerveza', 'cervezas', 'beer', 'ipa', 'lager', 'chela', 'stout', 'pilsen', 'schop'],
  },
  {
    id: 'licor',
    label: 'trago, cóctel o bar',
    match: [
      'pisco',
      'whisky',
      'whiskey',
      'vodka',
      'ron',
      'gin',
      'gin tonic',
      'tragos',
      'trago',
      'cocktail',
      'coctel',
      'cóctel',
      'bar',
      'shot',
      'shots',
      'tequila',
      'aperol',
      'mojito',
    ],
  },
  {
    id: 'colaciones',
    label: 'colaciones o packs',
    match: ['colacion', 'colaciones', 'despacho escolar', 'pack escolar', 'lonchera'],
  },
  {
    id: 'platos_preparados',
    label: 'platos preparados',
    match: ['plato preparado', 'platos preparados', 'comida casera', 'fuente de carga', 'menú ejecutivo', 'menu ejecutivo'],
  },
  { id: 'wok', label: 'wok o oriental', match: ['wok', 'fideos salteados', 'chop suey'] },
  { id: 'ramen', label: 'ramen', match: ['ramen', 'ramens'] },
].map((f) => ({
  id: f.id,
  label: f.label,
  match: [...new Set(f.match.map((k) => normalizeAssistantText(String(k))).filter((k) => k.length >= 2))].sort(
    (a, b) => b.length - a.length
  ),
}));

export function matchProductCategory(n) {
  let best = null;
  let bestLen = -1;
  for (const fam of PRODUCT_FAMILY_ALIASES) {
    for (const alias of fam.match) {
      if (alias.length < 2) continue;
      if (aliasMatchesInQuery(n, alias) && alias.length > bestLen) {
        best = { label: fam.label, keys: fam.match, familyId: fam.id };
        bestLen = alias.length;
      }
    }
  }
  return best;
}

export function filterProductsByCategoryKeys(products, keys, menuData = null) {
  const vis = visibleMenuProducts(products);
  const out = [];
  for (const p of vis) {
    const blob = getProductSearchBlob(p, menuData);
    if (keys.some((k) => k.length && aliasMatchesInBlob(blob, k))) out.push(p);
  }
  out.sort((a, b) => (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0));
  return out;
}

export function productTypeQueryIntent(n, trimmed) {
  if (/\b(no\s+quiero|nada\s+de|sin\s+gana)\b/.test(n)) return false;
  const shortType =
    trimmed.length <= 52 &&
    /\b(sushi|pizza|churrasco|hamburguesa|cerveza|completo|empanada|poke|temaki|hand\s*roll|taco|tacos|burrito|quesadilla|nacho|mechada|colacion|colaciones|pollo\s+asado|pollo\s+frito|papas|wok|ramen|sandwich|sándwich|pasta|licor|trago|mojito)\b/.test(
      n
    );
  return (
    /\b(tienen|hay|venden|sirven|dan|ofrecen|busco|quiero|pedir|algo\s+de|un\s+|una\s+|unos|unas)\b/.test(n) ||
    shortType
  );
}

export function productIngredientSearchBlob(product) {
  const chunks = [product?.name, product?.category];
  const incSum = product?.included_summary ?? product?.includedSummary;
  if (incSum != null && String(incSum).trim()) chunks.push(String(incSum).trim());
  const incItems = getProductIncludedItems(product);
  if (incItems.length) chunks.push(incItems.join(' '));
  const ups = getProductUpsellItems(product);
  if (ups.length) chunks.push(ups.join(' '));
  if (product?.description) chunks.push(product.description);
  const rem = listRemovable(product);
  if (rem?.length) chunks.push(rem.join(' '));
  const a = product?.add_ons;
  if (Array.isArray(a)) {
    chunks.push(
      a
        .map((x) => (typeof x === 'string' ? x : x?.name || ''))
        .filter(Boolean)
        .join(' ')
    );
  }
  return normalizeAssistantText(chunks.filter(Boolean).join(' '));
}

export function findProductsMatchingIngredientTerms(products, terms, menuData = null) {
  if (!terms?.length) return [];
  const groups = terms
    .map((t) => normalizeAssistantText(String(t)))
    .filter((t) => t.length >= 2)
    .map((t) => expandSynonymListWithPlurals(expandIngredientTermGroup(t)))
    .filter((g) => g.length);
  if (!groups.length) return [];
  const vis = visibleMenuProducts(products);
  const out = [];
  for (const p of vis) {
    const blob = getProductSearchBlob(p, menuData);
    const ok = groups.every((synonyms) => synonyms.some((s) => s.length && aliasMatchesInBlob(blob, s)));
    if (ok) out.push(p);
  }
  return out.slice(0, 8);
}

function tokenizeIngredientPhrase(raw) {
  return normalizeAssistantText(raw)
    .split(/[^a-z0-9ñ]+/)
    .filter((w) => w.length >= 3 && !ASSISTANT_STOP_WORDS.has(w));
}

function isMenuMetaIngredientQuery(n, terms, displayLabel) {
  const joined = normalizeAssistantText([displayLabel || '', ...(terms || [])].filter(Boolean).join(' '));
  if (/\b(menu\s+del\s+dia|plato\s+del\s+dia)\b/.test(joined)) return true;
  if (/\b(tiene|hay|tienen|algo\s+con)\b/.test(n)) {
    if (/\b(el\s+)?(menu|menú|carta)(\s+del\s+d(í|i)a|\s+del\s+dia)?\b/.test(n)) {
      if (!/\b(pollo|carne|pescado|queso|vegan|camar|champ|champi|huevo)\b/.test(n)) return true;
    }
  }
  if (terms?.length && terms.every((t) => INGREDIENT_META_TERM_BLOCKLIST.has(t))) return true;
  return false;
}

function packIngredientQuery(n, terms, displayLabel) {
  if (!terms?.length) return null;
  const label = (displayLabel || terms[0]).slice(0, 48).trim() || terms[0];
  if (isMenuMetaIngredientQuery(n, terms, label)) return null;
  return { terms, displayLabel: label };
}

/**
 * @returns {{ terms: string[], displayLabel: string } | null}
 */
function extractIngredientAvailabilityQuery(trimmed, n) {
  const deq = trimmed.replace(/^¿+/, '').replace(/\?+$/g, '').trim();

  const mAlgo = n.match(/\balgo\s+con\s+(.+)$/);
  if (mAlgo) {
    let rest = mAlgo[1].replace(/\?+$/, '').trim();
    rest = rest.replace(/\b(en el menu|en el menú|hoy|disponible)\b.*$/i, '').trim();
    const terms = tokenizeIngredientPhrase(rest);
    const packed = packIngredientQuery(n, terms, rest.slice(0, 48).trim() || terms[0]);
    if (packed) return packed;
  }

  const mTiene = deq.match(/^(tiene|tienen|hay|traen|venden)\s+(.+)$/i);
  if (mTiene) {
    let rest = mTiene[2].trim();
    rest = rest.replace(/\b(en el menu|en el menú|en la carta|disponible|hoy)\b.*$/i, '').trim();
    if (/\b(delivery|horario|whatsapp|menu|menú|precio|descuento)\b/.test(normalizeAssistantText(rest))) {
      return null;
    }
    const terms = tokenizeIngredientPhrase(rest);
    const packed = packIngredientQuery(n, terms, rest.slice(0, 48).trim() || terms[0]);
    if (packed) return packed;
  }

  if (deq.split(/\s+/).length <= 10 && /\bcon\b/.test(n)) {
    const mCon = n.match(/\bcon\s+([a-záéíóúñ0-9]+(?:\s+[a-záéíóúñ0-9]+){0,2})\s*$/i);
    if (mCon && !/\b(cuanto|cuánto|como|cómo|donde|dónde)\b/.test(n)) {
      const rest = mCon[1].trim();
      const terms = tokenizeIngredientPhrase(rest);
      const packed = packIngredientQuery(n, terms, rest.slice(0, 48).trim() || terms[0]);
      if (packed) return packed;
    }
  }

  return null;
}

export function isIngredientAvailabilityQuery(n, trimmed) {
  if (/\b(horario|abierto|cuanto|cuánto|cuesta|whatsapp|delivery\s+cuanto)\b/.test(n)) return false;
  return extractIngredientAvailabilityQuery(trimmed, n) != null;
}


function topSalesByCount(products, limit) {
  return [...visibleMenuProducts(products)]
    .sort((a, b) => (Number(b.sales_count) || 0) - (Number(a.sales_count) || 0))
    .slice(0, limit);
}

export function answerIngredientAvailabilityQuestion(trimmed, n, products, defaultFallback, menuData = null) {
  const parsed = extractIngredientAvailabilityQuery(trimmed, n);
  if (!parsed) return null;
  const hits = findProductsMatchingIngredientTerms(products, parsed.terms, menuData);
  const label = parsed.displayLabel;
  if (hits.length) {
    return {
      text: `Sí 👌 encontré opciones con ${label} en el menú de hoy 👇`,
      products: hits,
      fallbackKeys: defaultFallback,
    };
  }
  const alts = topSalesByCount(products, 4);
  const altLine =
    alts.length > 0
      ? `\n\nNo es lo mismo que ${label}, pero esto es de lo más pedido en el local por si querés otra idea 👇`
      : '';
  return {
    text: `No veo opciones con ${label} en el menú activo de hoy.${altLine}`,
    products: alts.length ? alts : null,
    fallbackKeys: ['catalog', 'popular_hint', 'main'],
  };
}
